import { forwardRef, memo, useState, useMemo, useCallback } from "react";
import { Form } from "react-bootstrap";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { slice } from "../../../Utils/stringUtils";
import { normalizeForFuzzy, isCloseMatch, getSearchMatchScore, multiTokenMatch } from "../../../Utils/aiUtils";
import CustomTooltip from "../Tooltip/CustomTooltip";
import "./dropdown.scss";

const CustomInput = (props) => {
  const { onKeyDown, isAddNew, onNoOptionsClick, options, maxLength } = props.selectProps;
  return (
    <components.Input
      {...props}
      maxLength={maxLength}
      onKeyDown={(e) => {
        if (onKeyDown) onKeyDown(e);

        if ((e.key === "Tab" || e.key === "Enter") && isAddNew && onNoOptionsClick && props.value) {
          // Get the current filtered options from the select state
          const filteredOptions = props.selectProps.options || [];
          const hasMatches = filteredOptions.some((o) =>
            o.label?.toLowerCase().includes(props.value.toLowerCase()),
          );

          if (!hasMatches) {
            onNoOptionsClick(props.value);
            if (props.selectProps && props.selectProps.onMenuClose) {
              props.selectProps.onMenuClose();
            }
            e.preventDefault();
            return;
          }
        }

        props.onKeyDown?.(e); // Preserve default behavior
      }}
      onBlur={(e) => {
        if (isAddNew && onNoOptionsClick && props.value) {
          const filteredOptions = props.selectProps.options || [];
          const hasMatches = filteredOptions.some((o) =>
            o.label?.toLowerCase().includes(props.value.toLowerCase()),
          );
          if (!hasMatches) {
            onNoOptionsClick(props.value);
          }
        }
        if (props.onBlur) props.onBlur(e);
      }}
    />
  );
};

const CustomCreatableInput = (props) => {
  const { onKeyDown, onBlur, maxLength } = props.selectProps;

  return (
    <components.Input
      {...props}
      maxLength={maxLength}
      onKeyDown={(e) => {
        if (onKeyDown) onKeyDown(e);
        props.onKeyDown?.(e);
      }}
      onBlur={(e) => {
        if (onBlur) onBlur(e);
        props.onBlur?.(e);
      }}
    />
  );
};

const CustomOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="d-flex flex-column">
        {/* <strong style={{ textTransform: "uppercase" }}>{slice(props?.data?.label || '', 20)}</strong> */}
        <strong style={{ textTransform: "uppercase" }}>{props?.data?.label}</strong>

        <CustomTooltip text={props?.data?.description || ""}>
          <small>{slice(props?.data?.description || "", 30)}</small>
        </CustomTooltip>
      </div>
    </components.Option>
  );
};

const customFilterOption = (option, inputValue) => {
  if (!inputValue) return true;
  const labelScore = getSearchMatchScore(inputValue, option?.label || "");
  if (labelScore > 0) return true;
  const descScore = getSearchMatchScore(inputValue, option?.data?.description || "");
  return descScore > 0;
};

const CustomNoOptionsMessage = (props) => {
  const { isAddNew, onNoOptionsClick } = props.selectProps;
  const inputValue = props.selectProps.inputValue || "";

  // Only show "No options" message if input has at least 1 character
  if (inputValue.length < 1) {
    return isAddNew ? (
      <components.NoOptionsMessage {...props}>
        <div style={{ padding: "4px 8px" }}>Type to search or add new item</div>
      </components.NoOptionsMessage>
    ) : (
      <components.NoOptionsMessage {...props}>
        <div style={{ padding: "4px 8px" }}>No options found</div>
      </components.NoOptionsMessage>
    );
  }

  return isAddNew ? (
    <components.NoOptionsMessage {...props}>
      <div
        className="no-options-message"
        onClick={() => {
          onNoOptionsClick?.(inputValue);
          props.selectProps.onMenuClose();
        }}
        style={{
          cursor: "pointer",
          color: "var(--primary)",
          padding: "4px 8px",
        }}
      >
        No options found. Click to add Item.
      </div>
    </components.NoOptionsMessage>
  ) : (
    <components.NoOptionsMessage {...props}>
      <div style={{ padding: "4px 8px" }}>No options found.</div>
    </components.NoOptionsMessage>
  );
};

const Dropdown = memo(
  forwardRef((props, ref) => {
    const {
      options,
      isMulti,
      isDisabled,
      isClearable,
      isLoading,
      isSearchable = true,
      name,
      isRtl,
      value,
      onChange,
      label,
      placeholder,
      isRequired,
      additonalSearch,
      onKeyDown,
      onNoOptionsClick,
      isAddNew,
      creatable,
      onInputChange,
      maxLength,
      ...restProps
    } = props;
    // selectedOption was redundant for a controlled component and caused infinite loops

    const [searchInputValue, setSearchInputValue] = useState("");

    const handleInputChange = useCallback(
      (newInputValue, actionMeta) => {
        if (actionMeta?.action === "input-change") {
          setSearchInputValue(newInputValue || "");
        } else if (actionMeta?.action === "menu-close" || actionMeta?.action === "input-blur") {
          setSearchInputValue("");
        }
        if (onInputChange) {
          onInputChange(newInputValue, actionMeta);
        }
      },
      [onInputChange],
    );

    const processedOptions = useMemo(() => {
      if (!additonalSearch || !searchInputValue || !Array.isArray(options)) {
        return options;
      }
      const query = searchInputValue.trim();
      if (!query) return options;

      const scored = [];
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const labelScore = getSearchMatchScore(query, opt?.label || "");
        const descScore = opt?.data?.description
          ? getSearchMatchScore(query, opt.data.description)
          : 0;
        const maxScore = Math.max(labelScore, descScore);
        if (maxScore > 0) {
          scored.push({ opt, score: maxScore, index: i });
        }
      }

      scored.sort((a, b) => b.score - a.score || a.index - b.index);
      return scored.map((s) => s.opt);
    }, [options, searchInputValue, additonalSearch]);

    const handleChange = (selectedOption) => {
      if (onChange) {
        if (isMulti) {
          // For multi-select, return array of values
          onChange(selectedOption ? selectedOption.map((option) => option.value) : []);
        } else {
          onChange(selectedOption ? selectedOption.value : "");
        }
      }
    };

    const customStyles = {
      control: (provided, state) => ({
        ...provided,
        boxShadow: state.isFocused ? null : provided.boxShadow,
        borderColor: state.isFocused ? "var(--border-light)" : provided.borderColor,
        "&:hover": {
          borderColor: state.isFocused ? "var(--secondary)" : provided.borderColor,
        },
      }),
    };

    const SelectComponent = creatable ? CreatableSelect : Select;

    return (
      <>
        <Form.Label className={isRequired ? "required" : ""} aria-label={label}>
          {label}
        </Form.Label>

        <SelectComponent
          ref={ref}
          className="basic-single mb-3"
          classNamePrefix="select"
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isRtl={isRtl}
          isSearchable={isSearchable}
          onKeyDown={onKeyDown}
          name={name}
          menuPosition="fixed" // Use fixed for dropdown visibility in modals/scrollable containers
          menuPlacement="bottom"
          menuPortalTarget={document.body} // Portal it to the body to fix positioning
          styles={{
            ...customStyles,
            menu: (provided) => ({
              ...provided,
              zIndex: 9999, // ensure it's above all UI
              minWidth: "100%",
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }), // also for portal
          }}
          components={{
            Option: CustomOption,
            Input: creatable ? CustomCreatableInput : CustomInput,
            NoOptionsMessage: CustomNoOptionsMessage,
          }}
          options={Array.isArray(processedOptions) ? processedOptions : []}
          getOptionLabel={({ label }) => label}
          getOptionValue={({ value }) => value}
          value={
            Array.isArray(options)
              ? isMulti
                ? options.filter(({ value }) => name?.some((v) => v == value))
                : (() => {
                    // Avoid `value == name` when name is "" matching numeric 0 (loose equality).
                    if (name === "" || name === null || name === undefined) {
                      return null;
                    }
                    return (
                      options.find(
                        ({ value }) => value === name || String(value) === String(name),
                      ) || (creatable && name ? { label: name, value: name } : null)
                    );
                  })()
              : null
          }
          onChange={handleChange}
          placeholder={
            placeholder === ""
              ? "Select"
              : placeholder?.toLowerCase()?.startsWith("select")
                ? placeholder
                : `Select ${placeholder || "Value"}`
          }
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
            border: 0,
            colors: {
              ...theme.colors,
              primary25: "var(--border-light)",
              primary: "var(--primary)",
            },
          })}
          filterOption={additonalSearch ? customFilterOption : undefined}
          isMulti={isMulti}
          onInputChange={handleInputChange}
          tabSelectsValue={true}
          isAddNew={isAddNew}
          onNoOptionsClick={onNoOptionsClick}
          maxLength={maxLength}
          {...restProps}
        />
      </>
    );
  }),
);

export default Dropdown;
